import { HEROES, HERO_IDS, HERO_TIERS, HERO_UNLOCKS, isHeroUnlocked, heroTier, type HeroBest, type HeroId } from '@knight/shared';
import * as S from '../app/state';
import { t, tn, td } from '../i18n';
import { Btn, HeroPortrait, Icon, skillTip, showTip, hideTip } from './common';
import { heroBestView, freeHeroSlot } from '../app/heroes';

const TIER_KEYS = ['label.tier0', 'label.tier1', 'label.tier2', 'label.tier3'];

function leaningOf(id: HeroId): string {
  return Object.entries(HEROES[id].tagWeights)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
    .slice(0, 4)
    .map(([tag]) => t('tag.' + tag) !== 'tag.' + tag ? t('tag.' + tag) : tag)
    .join(', ');
}

/** Requirement lines for a locked hero: "Revenant 7/10". */
function needsOf(id: HeroId, best: HeroBest): { text: string; ok: boolean }[] {
  return HERO_UNLOCKS[id].map((n) => ({ text: `${tn('hero', n.hero)} ${Math.min(best[n.hero] ?? 0, n.level)}/${n.level}`, ok: (best[n.hero] ?? 0) >= n.level }));
}

function HeroNode(props: { id: HeroId; best: HeroBest; selected: boolean; onSelect: () => void }) {
  const h = HEROES[props.id];
  const unlocked = isHeroUnlocked(props.best, props.id);
  const bestLevel = props.best[props.id] ?? 0;
  return (
    <div class={'tree-node' + (props.selected ? ' sel' : '') + (unlocked ? '' : ' locked')} onClick={props.onSelect} style={{ borderColor: props.selected ? h.accent : undefined }}>
      <HeroPortrait hero={props.id} size={64} />
      <b style={{ color: unlocked ? h.accent : 'var(--muted)' }}>{tn('hero', props.id)}</b>
      {unlocked ? (
        <span class="small muted">{bestLevel > 0 ? `${t('label.bestLevel')} ${bestLevel}` : leaningOf(props.id)}</span>
      ) : (
        <span class="small muted needs">
          {needsOf(props.id, props.best).map((n) => (
            <span class={n.ok ? 'ok' : ''}>{n.text}</span>
          ))}
        </span>
      )}
    </div>
  );
}

/** The hero tree: founders at the bottom, each higher tier unlocked by levels on the heroes below. */
export function HeroTree(props: { selected: HeroId; onSelect: (h: HeroId) => void }) {
  const best = heroBestView();
  const rows = [...HERO_TIERS].reverse();
  const pos = new Map<HeroId, { x: number; y: number }>();
  rows.forEach((row, ri) => row.forEach((id, ci) => pos.set(id, { x: ((ci + 0.5) / row.length) * 100, y: ((ri + 0.5) / rows.length) * 100 })));
  return (
    <div class="hero-tree" style={{ '--rows': rows.length } as Record<string, number>}>
      <svg class="tree-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
        {HERO_IDS.flatMap((id) =>
          HERO_UNLOCKS[id].map((n) => {
            const a = pos.get(n.hero)!;
            const b = pos.get(id)!;
            const ok = (best[n.hero] ?? 0) >= n.level;
            return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} class={ok ? 'ok' : ''} vector-effect="non-scaling-stroke" />;
          }),
        )}
      </svg>
      {rows.map((row, ri) => (
        <div class="tree-row">
          <div class="tree-tier">{t(TIER_KEYS[rows.length - 1 - ri] ?? 'label.tier3')}</div>
          <div class="tree-cards" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
            {row.map((id) => (
              <HeroNode id={id} best={best} selected={props.selected === id} onSelect={() => props.onSelect(id)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Description, kit and unlock state of one hero. */
export function HeroDetails(props: { id: HeroId }) {
  const id = props.id;
  const h = HEROES[id];
  const best = heroBestView();
  const unlocked = isHeroUnlocked(best, id);
  return (
    <div class="hero-details panel">
      <div class="row" style={{ alignItems: 'flex-start', gap: 14 }}>
        <HeroPortrait hero={id} size={96} />
        <div class="col grow" style={{ gap: 4 }}>
          <div class="row">
            <b style={{ fontFamily: 'var(--serif)', fontSize: 22, color: h.accent }}>{tn('hero', id)}</b>
            <span class="badge">{t(TIER_KEYS[heroTier(id)] ?? 'label.tier3')}</span>
            {!unlocked && <span class="badge off">{t('label.locked')}</span>}
          </div>
          <div class="desc" style={{ minHeight: 0 }}>{td('hero', id)}</div>
          <div class="small muted">
            {t('label.leaning')}: {leaningOf(id)}
          </div>
          <div class="row small">
            <Icon kind="item" id={h.weapon} size="sm" tip={{ title: tn('item', h.weapon), sub: t('label.weapon'), body: td('item', h.weapon) }} />
            <Icon kind="skill" id={h.startSkill} size="sm" tip={skillTip(h.startSkill, 1)} />
            <div class="icon sm clickable" style={{ background: h.trait.color }} onMouseEnter={(e) => showTip(e.currentTarget as HTMLElement, { title: t(`hero.${id}.trait`), sub: t('label.heroTrait'), body: t(`hero.${id}.traitDesc`), color: h.trait.color })} onMouseLeave={hideTip}>
              {h.trait.glyph}
            </div>
            <span class="muted">{t('label.startingKit')}</span>
          </div>
          <div class="small muted">
            <b class="gold">{t(`hero.${id}.trait`)}</b>: {t(`hero.${id}.traitDesc`)}
          </div>
          <div class="small muted">{t('label.heroWeapons', { list: h.weapons.map((w) => t('label.wclass.' + w)).join(', ') })}</div>
          <div class="row small wrap" style={{ gap: 4 }}>
            <span class="muted">{t('label.kitPool')}:</span>
            {h.skills.map((sid) => (
              <Icon kind="skill" id={sid} size="sm" tip={skillTip(sid, 1)} dim={sid !== h.startSkill} />
            ))}
          </div>
          {HERO_UNLOCKS[id].length > 0 && (
            <div class="small">
              <span class="muted">{t('label.unlockNeeds')}: </span>
              {needsOf(id, best).map((n, i) => (
                <span class={n.ok ? 'green' : 'blood'}>
                  {i > 0 ? ' · ' : ''}
                  {n.text}
                </span>
              ))}
              {!unlocked && <div class="muted">{t('label.heroLockedHint')}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Compact cards of the unlocked heroes (squad screen); locked ones are shown dimmed. */
export function HeroCards(props: { selected: HeroId; onSelect: (h: HeroId) => void; compact?: boolean }) {
  const best = heroBestView();
  return (
    <div class="heroes">
      {HERO_IDS.map((id) => {
        const h = HEROES[id];
        const unlocked = isHeroUnlocked(best, id);
        return (
          <div class={'hero-card panel' + (props.selected === id ? ' sel' : '') + (unlocked ? '' : ' locked')} onClick={() => unlocked && props.onSelect(id)}>
            <div class="row">
              <HeroPortrait hero={id} size={props.compact ? 48 : 64} />
              <div class="col" style={{ gap: 2 }}>
                <b style={{ fontFamily: 'var(--serif)', fontSize: 18, color: unlocked ? h.accent : 'var(--muted)' }}>{tn('hero', id)}</b>
                <span class="small muted">{unlocked ? `${t('label.leaning')}: ${leaningOf(id)}` : needsOf(id, best).map((n) => n.text).join(' · ')}</span>
              </div>
            </div>
            {!props.compact && <div class="desc">{td('hero', id)}</div>}
            {unlocked && (
              <div class="row small">
                <Icon kind="item" id={h.weapon} size="sm" tip={{ title: tn('item', h.weapon), sub: t('label.weapon'), body: td('item', h.weapon) }} />
                <Icon kind="skill" id={h.startSkill} size="sm" tip={skillTip(h.startSkill, 1)} />
                <div class="icon sm clickable" style={{ background: h.trait.color }} onMouseEnter={(e) => showTip(e.currentTarget as HTMLElement, { title: t(`hero.${id}.trait`), sub: t('label.heroTrait'), body: t(`hero.${id}.traitDesc`), color: h.trait.color })} onMouseLeave={hideTip}>
                  {h.trait.glyph}
                </div>
                <span class="muted">{t('label.startingKit')}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function HeroSelect(props: { confirmLabel: string; onConfirm: (h: HeroId) => void; onBack: () => void }) {
  const best = heroBestView();
  const selected = S.heroChoice.value;
  const unlocked = isHeroUnlocked(best, selected);
  // a new hero never overwrites a living one: it takes the chosen slot only when that slot is free
  const slot = freeHeroSlot();
  return (
    <div class="screen">
      <div class="card wide panel col tall heroselect">
        <div class="row">
          <h2>{t('label.heroTree')}</h2>
          <div class="grow" />
          <Btn small onClick={props.onBack}>
            {t('btn.back')}
          </Btn>
        </div>
        <div class="tree-scroll">
          <HeroTree selected={selected} onSelect={(h) => S.setHero(h)} />
        </div>
        <HeroDetails id={selected} />
        {/* the choice itself stays put while the details scroll under it */}
        <div class="col pickfoot">
          <Btn primary block disabled={!unlocked || slot < 0} onClick={() => unlocked && slot >= 0 && props.onConfirm(selected)}>
            {!unlocked ? t('label.locked') : slot < 0 ? t('label.slotsAllTaken') : `${props.confirmLabel}: ${tn('hero', selected)}`}
          </Btn>
          <div class={'small ' + (slot < 0 ? 'blood' : 'muted')} style={{ textAlign: 'center' }}>
            {slot < 0 ? t('label.slotsAllTakenHint') : t('label.newHeroSlot', { n: slot + 1 })}
          </div>
        </div>
      </div>
    </div>
  );
}
